import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ArrowRight, Clock, FileText, Building2, Layers, Briefcase, MapPin, Repeat, MessageSquareQuote, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Header from "@/components/Header";
import { AnnualUpsellBanner, useAnnualUpsell } from "@/components/AnnualUpsell";

const DD_REPORT_CARDS = [
  {
    key: "ti",
    icon: FileText,
    title: "Tenant Improvement (TI)",
    price: "Ask us",
    perLabel: "for project pricing",
    accent: "emerald",
    turnaround: "24–48 hours",
    features: [
      "Jurisdictional overview & AHJ contacts",
      "Required permits & approvals checklist",
      "Anticipated review timeline",
      "Compliance risks & mitigations",
      "Recommended submittal strategy",
      "Estimated fee schedule (rough order of magnitude)",
      "Open questions to confirm with AHJ",
      "Action items checklist",
      "Branded PDF download",
    ],
    cta: "Order a TI DD Report",
    href: "/hire?dd_type=ti&utm_source=pricing_dd",
  },
  {
    key: "groundup",
    icon: Building2,
    title: "Ground-Up Construction",
    price: "Ask us",
    perLabel: "for project pricing",
    accent: "indigo",
    turnaround: "24–48 hours",
    features: [
      "Everything in TI report",
      "Full entitlement & zoning analysis",
      "Multi-discipline permit sequencing",
      "Utility coordination guidance",
      "Site-specific compliance risks",
      "Branded PDF download",
    ],
    cta: "Order a Ground-Up DD Report",
    href: "/hire?dd_type=ground-up&utm_source=pricing_dd",
  },
  {
    key: "complex",
    icon: Layers,
    title: "Complex & Special Projects",
    price: "Ask us",
    perLabel: "scope-based pricing",
    accent: "amber",
    turnaround: "Typically 3–5 business days",
    projectTypes: "Hotels · High-Rise · Mixed-Use · Healthcare · Industrial · Special Projects",
    features: [
      "Scope-based pricing",
      "Extended research depth",
      "Multi-jurisdiction coordination",
      "Direct expert consultation included",
      "Custom branded PDF report",
    ],
    cta: "Request a Quote",
    href: "/hire?dd_type=complex&utm_source=pricing_dd",
  },
];

export default function Pricing() {
  const [params] = useSearchParams();
  const { status: upsell } = useAnnualUpsell();

  useEffect(() => {
    if (params.get("canceled")) toast.info("Checkout canceled — no charge made.");
  }, [params]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="pricing-page">
      <Header />
      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16">
        {upsell?.eligible && (
          <div className="max-w-4xl mb-8"><AnnualUpsellBanner status={upsell} /></div>
        )}

        {/* =========== DUE DILIGENCE REPORTS SECTION (TOP) =========== */}
        <section data-testid="dd-reports-section" className="mb-20 md:mb-28">
          <div className="max-w-3xl text-center mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 mb-5" data-testid="dd-reports-tag">
              <FileText className="w-3.5 h-3.5 text-indigo-700" />
              <span className="text-xs font-mono uppercase tracking-[0.18em] text-indigo-700">Due Diligence Reports · One-time purchase</span>
            </div>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Due Diligence Reports
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
              AI-generated, expert-reviewed due diligence reports for commercial projects — delivered in
              24–48 hours. The same research that typically takes days of consultant time, available on demand.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto" data-testid="dd-reports-grid">
            {DD_REPORT_CARDS.map((c) => <DDReportCard key={c.key} card={c} />)}
          </div>

          <div className="mt-10 max-w-4xl mx-auto text-center space-y-3" data-testid="dd-reports-notes">
            <p className="text-sm text-slate-600 leading-relaxed">
              <strong className="text-slate-900">Volume + recurring engagements unlock subscriber pricing.</strong>{" "}
              <Link to="/hire" className="text-emerald-700 underline hover:text-emerald-900">Ask about our subscription plans.</Link>
            </p>
            <p className="text-xs text-slate-500 italic leading-relaxed max-w-3xl mx-auto" data-testid="dd-reports-disclaimer">
              All DD reports are AI-generated using Coast To Coast&apos;s proprietary AHJ knowledge base.
              Verify all timelines, fees, and compliance pathways directly with the Authority Having Jurisdiction
              before use in contract or schedule decisions.
            </p>
          </div>
        </section>

        {/* =========== CUSTOM QUOTES SECTION =========== */}
        <section data-testid="custom-quotes-section" className="mt-20 md:mt-24">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-5">
              {/* Left band */}
              <div className="lg:col-span-2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8 md:p-10 flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 mb-5" data-testid="custom-quotes-tag">
                    <MessageSquareQuote className="w-3 h-3 text-emerald-300" />
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-200">Custom Quotes</span>
                  </div>
                  <h2 className="font-outfit text-3xl md:text-4xl font-bold leading-[1.08] tracking-tight">
                    Not every project fits a tier.
                  </h2>
                  <p className="mt-5 text-sm md:text-base text-slate-300 leading-relaxed">
                    Multi-state rollouts, recurring volume, or unusual jurisdiction mixes deserve a price built for them — not a plan picked off a menu.
                  </p>
                </div>
                <div className="mt-8 pt-6 border-t border-white/10">
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Tell us your business, your volume, and where you build. We&apos;ll come back with a fixed monthly or per-project quote — typically within 24 hours.
                  </p>
                </div>
              </div>

              {/* Right band */}
              <div className="lg:col-span-3 p-8 md:p-10">
                <h3 className="font-outfit text-xl md:text-2xl font-bold text-slate-900 mb-2">
                  How we build a custom quote
                </h3>
                <p className="text-sm text-slate-600 mb-7">
                  Our quotes are anchored on three real-world inputs — never a list price guess.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="custom-quotes-factors">
                  <CustomQuoteFactor
                    icon={Briefcase}
                    title="Your business"
                    body="Architect, GC, developer, REIT, national brand, or owner-operator — we tailor scope of work and reporting cadence to how your team actually operates."
                    testId="custom-quote-factor-business"
                  />
                  <CustomQuoteFactor
                    icon={Repeat}
                    title="Project volume"
                    body="One-off DD, 5 stores a year, or a 50-site national rollout. Higher recurring volume unlocks tighter per-project pricing and dedicated coordinator time."
                    testId="custom-quote-factor-volume"
                  />
                  <CustomQuoteFactor
                    icon={MapPin}
                    title="Geographic footprint"
                    body="Single AHJ, regional cluster, or all 50 states. We price for jurisdiction complexity, multi-state coordination overhead, and travel where it&apos;s warranted."
                    testId="custom-quote-factor-geography"
                  />
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-slate-200">
                  <div className="text-xs text-slate-500 leading-relaxed">
                    Quotes typically returned in <strong className="text-slate-700">24 hours</strong>. No commitment to review.
                  </div>
                  <Button asChild data-testid="custom-quotes-cta" className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-6">
                    <Link to="/hire?utm_source=pricing_custom_quote">
                      Request a custom quote<ArrowRight className="ml-2 w-3.5 h-3.5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Expediter callout */}
        <div className="mt-16 max-w-5xl mx-auto bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-2xl shadow-slate-900/20" data-testid="expediter-callout">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 mb-3">
              <Star className="w-3 h-3 text-emerald-400" />
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Expediter Service</span>
            </div>
            <h3 className="font-outfit text-2xl md:text-3xl font-bold leading-tight">
              Want us to handle the permit, not just answer the question?
            </h3>
            <p className="mt-3 text-sm text-slate-300 max-w-2xl">
              Hire Coast To Coast as your permit expediter on a per-project basis. We draft, submit, expedite,
              and close out — and your team watches it happen in your customer workspace.
            </p>
          </div>
          <Button asChild data-testid="expediter-cta" size="lg" className="bg-white text-slate-900 hover:bg-slate-100 h-12 px-7 whitespace-nowrap">
            <Link to="/hire">Request a quote<ArrowRight className="ml-2 w-4 h-4" /></Link>
          </Button>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto text-sm">
          {[
            { t: "Quotes in 24 hours", d: "Tell us about your business and we'll come back with a tailored quote — usually within one business day." },
            { t: "Nationwide coverage", d: "Permitting expertise across all 50 states, with deep playbooks in 3,221 U.S. counties." },
            { t: "Headquartered in DFW, Texas", d: "Based in Dallas–Fort Worth, serving clients coast to coast." },
          ].map((b) => (
            <div key={b.t} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="font-outfit font-semibold text-slate-900">{b.t}</div>
              <div className="mt-1 text-slate-600 text-xs">{b.d}</div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

function CustomQuoteFactor({ icon: Icon, title, body, testId }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-emerald-300 hover:bg-white transition-colors" data-testid={testId}>
      <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center mb-3">
        <Icon className="w-4 h-4" />
      </div>
      <h4 className="font-outfit text-base font-bold text-slate-900 mb-1.5">{title}</h4>
      <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function DDReportCard({ card }) {
  const Icon = card.icon;
  const accentMap = {
    emerald: { ring: "border-emerald-200 hover:border-emerald-400", iconBg: "bg-emerald-50 text-emerald-700", btn: "bg-emerald-600 hover:bg-emerald-500 text-white", priceColor: "text-slate-900", turnaroundBg: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    indigo: { ring: "border-indigo-300 ring-2 ring-indigo-100 hover:border-indigo-400", iconBg: "bg-indigo-50 text-indigo-700", btn: "bg-indigo-600 hover:bg-indigo-500 text-white", priceColor: "text-slate-900", turnaroundBg: "bg-indigo-50 text-indigo-800 border-indigo-200" },
    amber: { ring: "border-amber-200 hover:border-amber-400", iconBg: "bg-amber-50 text-amber-700", btn: "bg-slate-900 hover:bg-slate-800 text-white", priceColor: "text-slate-900", turnaroundBg: "bg-amber-50 text-amber-800 border-amber-200" },
  };
  const a = accentMap[card.accent] || accentMap.emerald;
  return (
    <div className={`relative bg-white border rounded-2xl p-6 flex flex-col transition-all hover:shadow-xl ${a.ring}`} data-testid={`dd-card-${card.key}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${a.iconBg}`}>
          <Icon className="w-5 h-5" />
        </div>
        <h3 className="font-outfit text-lg font-bold text-slate-900 leading-tight">{card.title}</h3>
      </div>

      <div className="mb-3 flex items-baseline gap-2">
        <span className={`font-outfit text-4xl font-bold ${a.priceColor}`} data-testid={`dd-card-${card.key}-price`}>{card.price}</span>
        <span className="text-xs text-slate-500">{card.perLabel}</span>
      </div>

      {/* Turnaround pill — prominently displayed near price */}
      <div className={`inline-flex items-center gap-1.5 self-start mb-4 px-2.5 py-1 rounded-full border ${a.turnaroundBg}`} data-testid={`dd-card-${card.key}-turnaround`}>
        <Clock className="w-3 h-3" />
        <span className="text-[11px] font-mono uppercase tracking-[0.14em]">{card.turnaround}</span>
      </div>

      {card.projectTypes && (
        <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-slate-500 mb-3 leading-snug" data-testid={`dd-card-${card.key}-types`}>{card.projectTypes}</p>
      )}

      <ul className="space-y-2 mb-6 flex-1">
        {card.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs text-slate-700">
            <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-none" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Button asChild data-testid={`dd-card-${card.key}-cta`} className={`w-full h-11 text-sm font-medium ${a.btn}`}>
        <Link to={card.href}>{card.cta}<ArrowRight className="ml-2 w-3.5 h-3.5" /></Link>
      </Button>
    </div>
  );
}
